import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getTrustedContactsApi,
  getTrustedCircleFilterOptionsApi,
  sendBulkRemindersApi,
} from '../api/trustedCircle.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import {
  defaultTrustedCircleFilters,
  toListParams,
  statusClass,
  reminderToastMessage,
  STATUS_OPTIONS,
  formatContactDate,
} from '../utils/trustedCircleFilters';

function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export default function TrustedCircles() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(defaultTrustedCircleFilters());
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedCircleId, setSelectedCircleId] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [systemTotal, setSystemTotal] = useState(null);
  const {
    page,
    limit,
    total,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  async function fetchContacts(pageNum = page, filtersSnapshot = filters) {
    setLoading(true);
    setError(null);
    const params = toListParams(filtersSnapshot, pageNum, limit);

    const result = await apiHandler(() => getTrustedContactsApi(params));
    if (result.success) {
      const data = unwrapData(result);
      setContacts(data?.contacts || []);
      applyPaginationMeta(data);
      const noFilters = !filtersSnapshot.state && !filtersSnapshot.status && !params.addedBy && !params.search;
      if (systemTotal === null && noFilters) {
        setSystemTotal(data?.total ?? 0);
      }
    } else {
      setError(result.message || 'Failed to fetch trusted contacts');
      setContacts([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await apiHandler(() => getTrustedCircleFilterOptionsApi());
      if (result.success) setFilterOptions(unwrapData(result));
    }
    loadOptions();
  }, []);

  useEffect(() => {
    fetchContacts(page);
  }, [page]);

  function applyFilters() {
    if (page !== 1) resetPage();
    fetchContacts(1, filters);
  }

  function resetFilters() {
    const cleared = defaultTrustedCircleFilters();
    setFilters(cleared);
    resetPage();
    fetchContacts(1, cleared);
    showToast('Filters cleared', 'info');
  }

  async function sendReminder(circleId) {
    const id = circleId || selectedCircleId;
    if (!id) {
      showToast('Select a contact row first', 'info');
      return;
    }
    const result = await apiHandler(() => sendBulkRemindersApi(id));
    if (result.success) {
      const data = unwrapData(result);
      showToast(reminderToastMessage(data), data?.sentCount > 0 ? 'success' : 'info');
    } else {
      showToast(result.message || 'Reminder failed', 'error');
    }
  }

  const stateOptions = filterOptions?.states?.length
    ? [{ value: '', label: 'Any state' }, ...filterOptions.states.map((s) => ({ value: s, label: s }))]
    : [{ value: '', label: 'Any state' }, ...['WI', 'TX', 'AZ', 'FL', 'CA', 'NY', 'IL'].map((s) => ({ value: s, label: s }))];

  const statusOptions = filterOptions?.statuses?.length
    ? [{ value: '', label: 'Any status' }, ...filterOptions.statuses]
    : STATUS_OPTIONS;

  const hasActiveFilters = !!(
    filters.state ||
    filters.status ||
    filters.addedBySearch.trim() ||
    filters.contactSearch.trim()
  );

  const emptyMessage =
    systemTotal === 0
      ? 'No trusted contacts yet — they are added when users set up their circle.'
      : hasActiveFilters
        ? 'No contacts match your filters.'
        : 'No contacts on this page.';

  return (
    <>
      <PageHeader inline trail={[{ label: 'Trusted Circles' }]} />

      <div className="fade-up">
        <div className="section-card">
          <div className="cm-toolbar">
            <div className="cm-toolbar-start">
              <select
                className="cm-filter-select"
                value={filters.state}
                onChange={(e) => setFilters((p) => ({ ...p, state: e.target.value }))}
                aria-label="State"
              >
                {stateOptions.map((o) => (
                  <option key={o.value || 'any'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <select
                className="cm-filter-select"
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                aria-label="Circle status"
              >
                {statusOptions.map((o) => (
                  <option key={o.value || 'any'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <div className="cm-search-wrap" style={{ minWidth: 180 }}>
                <i className="fas fa-user" aria-hidden />
                <input
                  type="text"
                  className="cm-search-input"
                  placeholder="Added by (user name)…"
                  value={filters.addedBySearch}
                  onChange={(e) => setFilters((p) => ({ ...p, addedBySearch: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>

              <div className="cm-search-wrap">
                <i className="fas fa-magnifying-glass" aria-hidden />
                <input
                  type="text"
                  className="cm-search-input"
                  placeholder="Search contact name, phone, email…"
                  value={filters.contactSearch}
                  onChange={(e) => setFilters((p) => ({ ...p, contactSearch: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>
            </div>
            <div className="cm-toolbar-end">
              <button type="button" className="cm-apply-btn" onClick={applyFilters} disabled={loading}>
                <i className="fas fa-check" /> Apply
              </button>
              <button type="button" className="cm-reset-link" onClick={resetFilters}>
                Reset filters
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                margin: '12px 16px',
                padding: '12px 16px',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                color: '#B91C1C',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span>{error}</span>
              <button
                type="button"
                className="action-btn btn-blue"
                style={{ padding: '6px 12px' }}
                onClick={() => fetchContacts(page)}
              >
                Retry
              </button>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            {loading ? (
              <p style={{ padding: 24, color: '#64748B' }}>Loading contacts…</p>
            ) : contacts.length === 0 ? (
              <div className="tc-empty" style={{ padding: 48 }}>
                <i className="fas fa-circle-nodes" />
                <p>{emptyMessage}</p>
              </div>
            ) : (
              <table className="data-table" style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Relationship</th>
                    <th>Invite status</th>
                    <th>Added by</th>
                    <th>Circle</th>
                    <th>State</th>
                    <th>Added on</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr
                      key={c.id}
                      className={`tc-row${selectedCircleId === c.circle?.id ? ' tc-row-selected' : ''}`}
                      onClick={() =>
                        setSelectedCircleId(selectedCircleId === c.circle?.id ? null : c.circle?.id)
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div className="tc-avatar">{getInitials(c.name)}</div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>
                              {c.name}
                            </div>
                            {c.isRegistered && (
                              <span style={{ fontSize: 10, color: '#1D4ED8', fontWeight: 600 }}>
                                Registered user
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#475569', fontFamily: "'DM Mono',monospace" }}>
                        {c.phoneDisplay || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>{c.email || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>{c.relationship || '—'}</td>
                      <td>
                        <span className={statusClass(c.inviteStatus)}>{c.inviteStatus}</span>
                      </td>
                      <td>
                        {c.addedBy?.id ? (
                          <Link
                            to={`/users/${c.addedBy.id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#1D4ED8',
                              textDecoration: 'none',
                            }}
                          >
                            {c.addedBy.name}
                          </Link>
                        ) : (
                          <span style={{ fontSize: 12, color: '#64748B' }}>{c.addedBy?.name || '—'}</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (c.circle?.id) navigate(`/trusted-circles/${c.circle.id}`);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontFamily: "'DM Mono',monospace",
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#074f78',
                          }}
                        >
                          {c.circle?.circleId || '—'}
                        </button>
                        <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>
                          <span className={statusClass(c.circle?.status)} style={{ fontSize: 10, padding: '2px 6px' }}>
                            {c.circle?.status}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="tc-state">{c.circle?.state || '—'}</span>
                      </td>
                      <td style={{ fontSize: 12, color: '#94A3B8' }}>{formatContactDate(c.addedAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="tc-row-btn"
                          title="View circle"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (c.circle?.id) navigate(`/trusted-circles/${c.circle.id}`);
                          }}
                        >
                          <i className="fas fa-eye" />
                        </button>
                        <button
                          type="button"
                          className="tc-row-btn"
                          title="Send reminder"
                          onClick={(e) => {
                            e.stopPropagation();
                            sendReminder(c.circle?.id);
                          }}
                        >
                          <i className="fas fa-bell" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <Pagination
            {...paginationProps}
            actions={
              <>
                <button type="button" className="action-btn btn-orange" onClick={() => sendReminder()}>
                  <i className="fas fa-bell" />
                  Send Reminder
                </button>
                <button
                  type="button"
                  className="action-btn btn-blue"
                  onClick={() =>
                    selectedCircleId
                      ? navigate(`/trusted-circles/${selectedCircleId}`)
                      : showToast('Select a row to open circle', 'info')
                  }
                >
                  <i className="fas fa-arrow-up-right-from-square" />
                  Open Circle
                </button>
              </>
            }
          />
        </div>
      </div>

      {toast && <div className={`cm-toast show ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
